import './style.less';

import { Box, Flex, Icon, Space, Text, useColorMode } from '@tonic-one/react';
import { ErrorIcon, SuccessIcon } from '@tonic-one/react-icons';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOrgStore } from 'src/models/useOrgModel';
import { useUtcOffsetStore } from 'src/models/useUtcOffsetModel';
import { getUtcTime } from 'src/utils/time';

import { AllNotificationDict } from './notification';
import OrgMenu from './OrgMenu';
import StatusMenu from './StatusMenu';
import { AllTaskDict } from './task';
import { TFunction } from 'i18next';

export default function MessageList(props: {
  isOpen: boolean;
  type: 'notification' | 'task';
  messages: AllNotificationDict | AllTaskDict;
}) {
  const { t } = useTranslation();

  const [colorMode] = useColorMode();
  const orgModel = useOrgStore();
  const timeModel = useUtcOffsetStore();

  const [selectedOrgId, setSelectedOrgId] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  useEffect(() => {
    setSelectedOrgId('all');
    setSelectedStatus('all');
  }, [props.isOpen]);

  return (
    <>
      <Flex
        direction="row"
        align="center"
        justify="flex-start"
        py="8px"
        px="16px"
      >
        <OrgMenu
          orgs={orgModel.orgs || []}
          selectedOrgId={selectedOrgId}
          setSelectedOrgId={setSelectedOrgId}
        />
        <Space width="26px"></Space>
        <StatusMenu
          selectedStatus={selectedStatus}
          setSelectedStatus={setSelectedStatus}
        />
      </Flex>
      <Box overflowX="hidden" overflowY="scroll" height="400px">
        {renderItems(
          t,
          props.type,
          props.messages,
          orgModel.orgs || [],
          selectedOrgId,
          selectedStatus,
          timeModel.offset,
          colorMode
        )}
      </Box>
    </>
  );
}

function renderItems(
  t: TFunction<'translation'>,
  i18nPrefix: string,
  allItems: AllNotificationDict | AllTaskDict,
  orgs: any[],
  selectedOrgId: string,
  selectedStatus: string,
  timeZone: string,
  colorMode: 'dark' | 'light'
) {
  const isDark = colorMode === 'dark';
  // const unread = true;
  const nodes = [];

  const orgMap: { [orgId: string]: string } = {};
  for (const org of orgs) {
    orgMap[org.key] = org.value;
  }

  for (const [orgId, dict] of Object.entries(allItems)) {
    if (selectedOrgId !== 'all' && selectedOrgId !== orgId) {
      continue;
    }

    for (const item of Object.values(dict)) {
      if (item.hidden) {
        continue;
      }
      if (
        selectedStatus !== 'all' &&
        !item.status.toLowerCase().includes(selectedStatus)
      ) {
        continue;
      }
      const time =
        getUtcTime(
          item.time?.split('.')[0],
          timeZone,
          t('common.momentDateFormat')
        ) || '--';

      nodes.push(
        <Flex
          direction="row"
          align="flex-start"
          justify="flex-start"
          py="15px"
          px="18px"
          borderTop={`2px solid ${isDark ? '#212121' : '#F2F2F2'}`}
          cursor="pointer"
          key={orgId + item.type}
          className={
            isDark ? 'notification-item-dark' : 'notification-item-light'
          }
        >
          <Box>
            {item.status.toUpperCase().includes('FAILURE') ? (
              <Icon as={ErrorIcon} color="#F24C4F" mr="2x" />
            ) : (
              <Icon as={SuccessIcon} color="#04C45A" mr="2x" />
            )}
          </Box>
          <Box>
            <Text fontSize="14px">
              {t(
                `${i18nPrefix}.${item.type.toLowerCase()}.${item.status
                  .toLowerCase()
                  .replace(':', ';')}`,
                t('common.unknownError')
              )}
              {/* Avoid use ':' in l10n, It means namespace in react-i18next */}
            </Text>
            <Box
              color={isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.65)'}
              fontSize="12px"
            >
              {time} • {orgMap[orgId] || '--'}
            </Box>
          </Box>
        </Flex>
      );
    }
  }

  return nodes;
}
